/**
 * Safety gate form — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Fields: @coordio/ui FormField (label + ⓘ tooltip + error + required asterisk)
 * Cards:  @coordio/ui FormSection (title + description + bordered body)
 * Toggle: @coordio/ui FormChoiceField (label + Switch in horizontal row)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Shield, Settings, FileJson } from 'lucide-react';

import {
  CoordioDropdown,
  FormField,
  Input,
  Switch,
  Textarea,
} from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../layouts/PageFormLayout';
import { safetyGateService, SafetyGateSeverity } from '../../services/safetyGateService';
import { useNotificationStore } from '../../store/notificationStore';

const severityOptions = [
  { label: 'Low', value: SafetyGateSeverity.Low },
  { label: 'Medium', value: SafetyGateSeverity.Medium },
  { label: 'High', value: SafetyGateSeverity.High },
  { label: 'Critical', value: SafetyGateSeverity.Critical },
];

export const SafetyGateForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [actionPattern, setActionPattern] = useState('');
  const [severity, setSeverity] = useState<SafetyGateSeverity>(SafetyGateSeverity.Medium);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [requiredApprovers, setRequiredApprovers] = useState<number | undefined>(undefined);
  const [autoApprovalTimeoutMinutes, setAutoApprovalTimeoutMinutes] = useState<number | undefined>(undefined);
  const [approverRolesJson, setApproverRolesJson] = useState('');
  const [conditionJson, setConditionJson] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isEdit && id) {
      loadGate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadGate = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await safetyGateService.getSafetyGate(id);
      setName(data.name);
      setDescription(data.description || '');
      setActionPattern(data.actionPattern);
      setSeverity(data.severity);
      setRequiresApproval(data.requiresApproval);
      setRequiredApprovers(data.requiredApprovers);
      setAutoApprovalTimeoutMinutes(data.autoApprovalTimeoutMinutes);
      setApproverRolesJson(data.approverRolesJson || '');
      setConditionJson(data.conditionJson || '');
      setIsActive(data.isActive);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load safety gate';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!actionPattern.trim()) {
      setError('Action pattern is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        actionPattern: actionPattern.trim(),
        severity,
        requiresApproval,
        requiredApprovers: requiredApprovers || undefined,
        autoApprovalTimeoutMinutes: autoApprovalTimeoutMinutes || undefined,
        approverRolesJson: approverRolesJson.trim() || undefined,
        conditionJson: conditionJson.trim() || undefined,
        isActive,
      };

      if (isEdit && id) {
        await safetyGateService.updateSafetyGate(id, data);
        addNotification('Safety gate updated successfully', 'success');
      } else {
        await safetyGateService.createSafetyGate(data);
        addNotification('Safety gate created successfully', 'success');
      }
      navigate('/safety-gates');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to save safety gate';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit Safety Gate' : 'Create Safety Gate'}
      subtitle="Define rules that pause or block dangerous tool calls, requiring human approval before execution."
      data-testid="safety-gate-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/safety-gates' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate('/safety-gates'),
        saveLabel: isEdit ? 'Save changes' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <Shield size={14} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'approval', label: 'Approval', icon: <Settings size={14} /> },
            { id: 'advanced', label: 'Advanced', icon: <FileJson size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, severity, and the action pattern this gate intercepts.',
          icon: <Shield size={16} />,
          content: (
            <FormSection
              title="Gate identity"
              description="Automations match the action pattern at runtime. The name is immutable after creation."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Unique identifier for this safety gate rule."
                  data-testid="safety-gate-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isEdit}
                    placeholder="e.g. block-destructive-ops"
                    data-testid="safety-gate-name"
                  />
                </FormField>

                <FormField label="Severity" description="Controls the priority of the intercepted action.">
                  <CoordioDropdown
                    options={severityOptions}
                    value={severity}
                    onChange={(val) => setSeverity((val ?? SafetyGateSeverity.Medium) as SafetyGateSeverity)}
                    clearable={false}
                    data-testid="safety-gate-severity"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Description" description="Optional context about what this gate protects.">
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    data-testid="safety-gate-description"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField
                  label="Action Pattern (Regex)"
                  required
                  description="Regular expression to match tool or action names at runtime."
                >
                  <Input
                    className="w-full"
                    value={actionPattern}
                    onChange={(e) => setActionPattern(e.target.value)}
                    placeholder="e.g., ^delete|^remove|^drop"
                    data-testid="safety-gate-actionPattern"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={isActive}
                        onCheckedChange={(checked) => setIsActive(checked)}
                        data-testid="safety-gate-isActive"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'approval',
          title: 'Approval',
          description: 'Configure whether intercepted actions require human approval and how many approvers are needed.',
          icon: <Settings size={16} />,
          content: (
            <FormSection
              title="Approval settings"
              description="When approval is required, the action is paused until the configured number of approvers accept it."
            >
              <div className="mb-4">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch
                        checked={requiresApproval}
                        onCheckedChange={(checked) => setRequiresApproval(checked)}
                        data-testid="safety-gate-requiresApproval"
                      />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Requires Approval</span>
                    </div>
                  </div>
                </div>
              </div>

              {requiresApproval && (
                <>
                  <div className="grid gap-5 items-end sm:grid-cols-2">
                    <FormField label="Required Approvers" description="Number of approvals needed before the action proceeds.">
                      <Input
                        className="w-full"
                        type="number"
                        value={requiredApprovers || ''}
                        onChange={(e) => setRequiredApprovers(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                        min={1}
                        data-testid="safety-gate-requiredApprovers"
                      />
                    </FormField>

                    <FormField label="Approval Timeout (minutes)" description="Auto-reject after this many minutes (optional).">
                      <Input
                        className="w-full"
                        type="number"
                        value={autoApprovalTimeoutMinutes || ''}
                        onChange={(e) => setAutoApprovalTimeoutMinutes(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                        min={1}
                        data-testid="safety-gate-approvalTimeout"
                      />
                    </FormField>
                  </div>

                  <div className="mt-5">
                    <FormField label="Approver Roles" description='JSON array of roles that can approve, e.g., ["Admin", "Security"].'>
                      <Textarea
                        className="w-full"
                        value={approverRolesJson}
                        onChange={(e) => setApproverRolesJson(e.target.value)}
                        rows={2}
                        placeholder='["Admin", "Security"]'
                        data-testid="safety-gate-approverRoles"
                      />
                    </FormField>
                  </div>
                </>
              )}
            </FormSection>
          ),
        },
        {
          id: 'advanced',
          title: 'Advanced',
          description: 'Additional conditions expressed as a JSON predicate.',
          icon: <FileJson size={16} />,
          content: (
            <FormSection
              title="Condition JSON"
              description="An optional JSON object evaluated at runtime. The gate fires only when the pattern matches AND the condition is satisfied."
            >
              <FormField label="Condition" description="Additional conditions as a JSON object. Leave empty to match all.">
                <Textarea
                  className="w-full font-mono text-[13px]"
                  value={conditionJson}
                  onChange={(e) => setConditionJson(e.target.value)}
                  rows={4}
                  placeholder='{"key": "value"}'
                  data-testid="safety-gate-conditionJson"
                />
              </FormField>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
